import { Icon, Tab, TabId, TabPanel, Tabs, Tooltip } from '@blueprintjs/core';
import styles from './ApplicationDataApplicantsTabs.module.css';
import {
  ace_hooks,
  useLoanApplication,
  useLoanApplicationRevision,
  usePerson,
} from '@axo/shared/data-access/hooks';
import { ApplicationDataMainApplicantTabPanel } from './ApplicationDataMainApplicantTabPanel';
import { ApplicationDataCoApplicantTabPanel } from './ApplicationDataCoApplicantTabPanel';
import { ApplicationDataCommentTabPanel } from './ApplicationDataCommentTabPanel';
import { Product } from '@axo/shared/data-access/types';
import {
  ApplicationDataTabType,
  useApplicationDataStore,
} from '../../../store/applicationDataStore';
import { useCallback, useEffect, useState } from 'react';
import {
  getApplicantsFormStore,
  SelectedApplicantTab,
  useApplicantsFormStore,
} from '../../../forms/applicants/store';

const TAB_COMMENT = 'comment-tab';

interface ApplicationDataApplicantsTabsProps {
  applicationID: string;
}

export const ApplicationDataApplicantsTabs: React.FC<
  ApplicationDataApplicantsTabsProps
> = ({ applicationID }) => {
  const TABS_PARENT_ID = `application-data-tabs-${applicationID}`;

  const { data: latestApplicationData } = useLoanApplication(applicationID);
  const { selectedTab: selectedRequestTab } =
    useApplicationDataStore(applicationID);

  const {
    selectedApplicant,
    actions: { setSelectedApplicant },
  } = useApplicantsFormStore(applicationID);
  const [selectedTab, setSelectedTab] = useState<
    SelectedApplicantTab | typeof TAB_COMMENT
  >(selectedApplicant);

  const isSecuredLending =
    latestApplicationData?.Product === Product.SecuredLoan;
  const { data: axoVersionData, isLoading: isLoadingAxoVersionData } =
    ace_hooks.useAxoVersion(isSecuredLending ? applicationID : undefined);

  const { data: applicationRevisionData } = useLoanApplicationRevision({
    applicationID,
    revision: axoVersionData?.OriginalRevision,
  });
  const revisionedApplicationData =
    isSecuredLending && !isLoadingAxoVersionData && axoVersionData
      ? applicationRevisionData
      : undefined;

  const applicationData =
    selectedRequestTab.type === ApplicationDataTabType.ORIGINAL &&
    isSecuredLending
      ? (revisionedApplicationData ?? latestApplicationData)
      : latestApplicationData;
  const { data: applicantData } = usePerson(applicationData?.PersonID);
  const { data: coApplicantData } = usePerson(
    applicationData?.CoApplicantPersonID,
  );

  useEffect(() => {
    const store = getApplicantsFormStore(applicationID);

    store.subscribe((state, prevState) => {
      if (state.selectedApplicant !== prevState.selectedApplicant) {
        setSelectedTab(state.selectedApplicant);
      }
    });
  });

  const onTabChange = useCallback(
    (tabID: TabId) => {
      if (tabID === TAB_COMMENT) {
        setSelectedTab(TAB_COMMENT);
      } else if (tabID === SelectedApplicantTab.CO_APPLICANT) {
        setSelectedTab(tabID);
        setSelectedApplicant(tabID);
      } else {
        setSelectedTab(SelectedApplicantTab.MAIN_APPLICANT);
        setSelectedApplicant(SelectedApplicantTab.MAIN_APPLICANT);
      }
    },
    [setSelectedApplicant],
  );

  const applicantName =
    (applicantData &&
      `${applicantData.FirstName ?? ''} ${applicantData.LastName ?? ''}`.trim()) ||
    undefined;
  const coApplicantName =
    (coApplicantData &&
      `${coApplicantData.FirstName ?? ''} ${coApplicantData.LastName ?? ''}`.trim()) ||
    undefined;

  if (!applicationData) return null;

  return (
    <>
      <Tabs
        key={TABS_PARENT_ID}
        id={TABS_PARENT_ID}
        onChange={onTabChange}
        selectedTabId={selectedTab}
        className={styles.tabs}
      >
        <Tab
          id={SelectedApplicantTab.MAIN_APPLICANT}
          title={
            <div>
              <Tooltip placement="top" content="Main applicant">
                <span className={styles.tab}>
                  <Icon icon="person" />
                  <span>{applicantName ?? 'Main Applicant Data'}</span>
                </span>
              </Tooltip>
            </div>
          }
        />
        {(applicationData.CoApplicantPersonID ||
          applicationData.CoApplicantCustomerID) && (
          <Tab
            id={SelectedApplicantTab.CO_APPLICANT}
            title={
              <div>
                <Tooltip placement="top" content="Co-applicant">
                  <span className={styles.tab}>
                    <Icon icon="people" />
                    <span>{coApplicantName ?? 'Co-Applicant Data'}</span>
                  </span>
                </Tooltip>
              </div>
            }
          />
        )}
        {applicationData.Product === Product.SecuredLoan ? (
          <Tab
            id={TAB_COMMENT}
            title={
              <div>
                <span className={styles.tab}>
                  <Icon icon="comment" />
                  <span>Application comment</span>
                </span>
              </div>
            }
          />
        ) : null}
      </Tabs>
      {selectedTab === SelectedApplicantTab.MAIN_APPLICANT && (
        <TabPanel
          key={SelectedApplicantTab.MAIN_APPLICANT}
          id={SelectedApplicantTab.MAIN_APPLICANT}
          selectedTabId={selectedTab}
          parentId={TABS_PARENT_ID}
          className={styles.tabPanel}
          panel={
            <ApplicationDataMainApplicantTabPanel
              applicationID={applicationID}
            />
          }
        />
      )}
      {selectedTab === SelectedApplicantTab.CO_APPLICANT &&
        (applicationData.CoApplicantCustomerID ||
          applicationData.CoApplicantPersonID) && (
          <TabPanel
            key={SelectedApplicantTab.CO_APPLICANT}
            id={SelectedApplicantTab.CO_APPLICANT}
            selectedTabId={selectedTab}
            parentId={TABS_PARENT_ID}
            className={styles.tabPanel}
            panel={
              <ApplicationDataCoApplicantTabPanel
                applicationID={applicationID}
                coApplicantCustomerID={
                  applicationData.CoApplicantCustomerID ?? undefined
                }
                coApplicantPersonID={
                  applicationData.CoApplicantPersonID ?? undefined
                }
              />
            }
          />
        )}
      {selectedTab === TAB_COMMENT && (
        <TabPanel
          key={TAB_COMMENT}
          id={TAB_COMMENT}
          selectedTabId={selectedTab}
          parentId={TABS_PARENT_ID}
          className={styles.tabPanel}
          panel={
            <ApplicationDataCommentTabPanel applicationID={applicationID} />
          }
        />
      )}
    </>
  );
};
